import Icon from "@/components/Icon";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { genBlock } from "@/utils/bem";
import { Button, chakra, Flex, Text, useBoolean } from "@chakra-ui/react";
import classNames from "classnames";
import React, { RefObject, useEffect } from "react";
import { ReactComponent as ArrowSvg } from "@/assets/icons/arrow-down.svg";
import { albumStore } from "@/modules/album/store";
import { CreateAlbumModal } from "@/modules/album/components/CreateModal";
import { observer } from "mobx-react-lite";
import { WithClassName } from "@/types/base";
import { parseLabels } from "@/utils/misc";
import { useTranslate } from "@/utils/lang";
import { AlbumWords } from "@/utils/lang/album";
import { ReactComponent as AddSvg } from "@/assets/icons/pure_add.svg";
import { useAlbumListMenuScroll } from "@/modules/album/hook/useAlbumListMenuScroll";

type BoardImageAlbumOpProps = WithClassName<{
  albumId?: number;
  albumName?: string;
  albumImageId?: number;
  alias: string;
  imageUrl: string;
  metaJson: any;
  containerRef?: RefObject<HTMLElement | null>;
  onMoveAlbum?: () => void;
  onRemove?: () => void;
  onAdd?: () => void;
  onCreateAlbum?: () => void;
}>;
const block = genBlock("c-creator-album-op");
const AlbumOp: React.FC<BoardImageAlbumOpProps> = ({
  albumId,
  albumName,
  albumImageId,
  alias,
  imageUrl,
  metaJson,
  className,
  containerRef,
  onMoveAlbum,
  onRemove,
  onAdd,
  onCreateAlbum,
}) => {
  const { translate } = useTranslate();
  const inAlbum = !!albumId && !!albumImageId;
  // 弹窗
  const [modalOpen, modalOp] = useBoolean(false);
  const [isLoading, loadingOp] = useBoolean(false);

  // 修改分组
  const handleMovePictures = (id: string) => {
    if (!albumId || !albumImageId) return;
    if (id === "create") {
      modalOp.on();
    } else {
      const fromId = albumId!,
        toId = +id;
      if (fromId === toId) return;
      albumStore.movePictures(fromId, toId, [albumImageId]).then(() => {
        onMoveAlbum?.();
      });
    }
  };

  const handleCollect = async () => {
    loadingOp.on();
    if (!inAlbum) {
      // 加入画册
      const res = await albumStore.addPictures([
        {
          board_alias: alias,
          meta_json: JSON.stringify(metaJson ?? {}),
          image_url: imageUrl,
          source: "board",
          is_ai: parseLabels(metaJson)?.isAI ? 1 : 2,
        },
      ]);
      if (res.code === 0) {
        onAdd?.();
      }
    } else {
      // 移出画册
      const res = await albumStore.deletePictures(albumId, [albumImageId!]);
      if (res.code === 0) {
        onRemove?.();
      }
    }
    loadingOp.off();
  };

  const { scrollContainerRef } = useAlbumListMenuScroll();

  useEffect(() => {
    if (!albumStore.albumListOptions?.length) {
      albumStore.fetchAlbums();
    }
  }, []);

  return (
    <Flex
      className={classNames(className, block())}
      w="100%"
      justifyContent="space-between"
      alignItems="center"
    >
      {inAlbum ? (
        <Flex alignItems="center">
          <Text>{translate(AlbumWords.alreadyInAlbum)}：</Text>
          <OperationMenu
            containerRef={containerRef}
            menuListRef={scrollContainerRef as any}
            menuList={albumStore.albumListOptions}
            onSelect={handleMovePictures}
            staticList={[
              {
                key: "create",
                content: (
                  <Flex alignItems="center">
                    <Icon svg={AddSvg} squared={false} fillbyCurrentColor mr="4px" />
                    {translate(AlbumWords.newAlbum)}{" "}
                  </Flex>
                ),
              },
            ]}
            value={albumId?.toString() ?? ""}
            variant="creatorMoveImage"
            styles={{
              divider: {
                mt: "6px",
              },
            }}
          >
            <Flex alignItems="center" ml="12px">
              <Text as="span">{albumName}</Text>
              <Icon svg={ArrowSvg} fontSize="14px" ml="8px" color="white" fillbyCurrentColor />
            </Flex>
          </OperationMenu>
        </Flex>
      ) : null}
      <Button
        variant="dark.solid"
        w="auto"
        p="4px 8px"
        border="1px solid #585858"
        bg="bg.boardBg"
        _hover={{
          bg: "bg.boardBg",
        }}
        disabled={isLoading}
        isLoading={isLoading}
        loadingText={
          !inAlbum ? translate(AlbumWords.addToAlbum) : translate(AlbumWords.removeFromAlbum)
        }
        className={classNames(block("btn"), { add: !inAlbum, remove: inAlbum })}
        onClick={handleCollect}
      >
        {!inAlbum ? translate(AlbumWords.addToAlbum) : translate(AlbumWords.removeFromAlbum)}
      </Button>
      <CreateAlbumModal isOpen={modalOpen} onClose={modalOp.off} onCreate={onCreateAlbum} />
    </Flex>
  );
};

export const BoardImageAlbumOp = chakra(observer(AlbumOp));
